// Onboarding em 5 passos e checklist de primeira promocao.
//
// Funcoes puras, sem I/O: o painel persiste o progresso via RPC
// save_onboarding_progress e le o estado real da conta via get_setup_status
// (migration 20261003700002). Este modulo so decide transicoes e contagem.
/** Passos do wizard, na ordem em que aparecem. */
export const ONBOARDING_STEPS = ["empresa", "canal", "plataformas", "credenciais", "grupos"];
export const INITIAL_ONBOARDING_PROGRESS = Object.freeze({
    currentStep: "empresa",
    completedSteps: [],
    skippedSteps: [],
    selectedPlatforms: [],
    finished: false,
});
const MAX_PLATFORMS = 20;
const PLATFORM_CODE = /^[a-z0-9_]{2,32}$/;
export function isOnboardingStep(value) {
    return typeof value === "string" && ONBOARDING_STEPS.includes(value);
}
function stepsFrom(raw) {
    if (!Array.isArray(raw))
        return [];
    const valid = raw.filter(isOnboardingStep);
    // Ordem canonica e sem repeticao, independente do que veio do banco.
    return ONBOARDING_STEPS.filter((step) => valid.includes(step));
}
function platformsFrom(raw) {
    if (!Array.isArray(raw))
        return [];
    const codes = raw.filter((p) => typeof p === "string" && PLATFORM_CODE.test(p));
    return [...new Set(codes)].slice(0, MAX_PLATFORMS);
}
/** Normaliza a linha crua de onboarding_progress. null = conta sem progresso. */
export function normalizeOnboardingProgress(raw) {
    if (raw === null || typeof raw !== "object")
        return null;
    const row = raw;
    return {
        currentStep: isOnboardingStep(row["current_step"]) ? row["current_step"] : "empresa",
        completedSteps: stepsFrom(row["completed_steps"]),
        skippedSteps: stepsFrom(row["skipped_steps"]),
        selectedPlatforms: platformsFrom(row["selected_platforms"]),
        finished: row["finished_at"] !== null && row["finished_at"] !== undefined,
    };
}
function stepIndex(step) {
    return ONBOARDING_STEPS.indexOf(step);
}
export function nextOnboardingStep(step) {
    return ONBOARDING_STEPS[stepIndex(step) + 1] ?? null;
}
export function previousOnboardingStep(step) {
    const index = stepIndex(step);
    return index > 0 ? (ONBOARDING_STEPS[index - 1] ?? null) : null;
}
function withStep(list, step) {
    return ONBOARDING_STEPS.filter((s) => s === step || list.includes(s));
}
function withoutStep(list, step) {
    return list.filter((s) => s !== step);
}
function advance(progress, completed) {
    const step = progress.currentStep;
    const next = nextOnboardingStep(step);
    return {
        ...progress,
        completedSteps: completed ? withStep(progress.completedSteps, step) : withoutStep(progress.completedSteps, step),
        skippedSteps: completed ? withoutStep(progress.skippedSteps, step) : withStep(progress.skippedSteps, step),
        currentStep: next ?? step,
        finished: next === null,
    };
}
/** Aplica uma acao do wizard. Imutavel: sempre devolve um novo objeto.
 *  "goto" so aceita voltar para um passo ja visitado (anterior ao atual). */
export function applyOnboardingAction(progress, action, target) {
    switch (action) {
        case "complete":
            return advance(progress, true);
        case "skip":
            return advance(progress, false);
        case "back": {
            const previous = previousOnboardingStep(progress.currentStep);
            return previous ? { ...progress, currentStep: previous, finished: false } : progress;
        }
        case "goto":
            if (!target || stepIndex(target) > stepIndex(progress.currentStep))
                return progress;
            return { ...progress, currentStep: target, finished: false };
    }
}
/** Grava as lojas escolhidas (mesma validacao do banco). */
export function withSelectedPlatforms(progress, platforms) {
    return { ...progress, selectedPlatforms: platformsFrom([...platforms]) };
}
/** O wizard so aparece para conta nova: tem linha de progresso e nao terminou.
 *  Contas antigas recebem a linha ja finalizada no backfill da migration. */
export function shouldShowOnboarding(progress) {
    return progress !== null && !progress.finished;
}
// ---------------------------------------------------------------------------
// Checklist "falta pouco para a primeira promocao"
// ---------------------------------------------------------------------------
export const SETUP_CHECKLIST_KEYS = ["channel", "store", "destination", "source", "firstLink"];
const STATUS_COLUMN = Object.freeze({
    channel: "channel_connected",
    store: "store_credential_valid",
    destination: "destination_chosen",
    source: "source_monitored",
    firstLink: "first_link_generated",
});
/** Normaliza a resposta crua da RPC: so true literal conta como feito. */
export function normalizeSetupStatus(raw) {
    const row = raw !== null && typeof raw === "object" ? raw : {};
    const entries = SETUP_CHECKLIST_KEYS.map((key) => [key, row[STATUS_COLUMN[key]] === true]);
    return Object.freeze(Object.fromEntries(entries));
}
export function buildSetupChecklist(status) {
    const items = SETUP_CHECKLIST_KEYS.map((key) => ({ key, done: status[key] }));
    const doneCount = items.filter((item) => item.done).length;
    return {
        items,
        doneCount,
        total: items.length,
        isComplete: doneCount === items.length,
        nextKey: items.find((item) => !item.done)?.key ?? null,
    };
}
//# sourceMappingURL=onboarding.js.map